import { Phone, Mail, MessageCircle, Globe, Download, ArrowUpRight } from "lucide-react";

/**
 * Premium digitale visitekaart voor JYNOVA founders.
 * person: { name, role, phone, phoneWa, email, website, photo, vcf }
 */
export default function BusinessCard({ person }) {
  const telHref = `tel:${person.phone.replace(/\s+/g, "")}`;
  const waHref = `https://wa.me/${person.phoneWa}`;
  const mailHref = `mailto:${person.email}`;
  const siteHref = person.website.startsWith("http")
    ? person.website
    : `https://${person.website}`;

  return (
    <main
      className="relative min-h-screen w-full overflow-hidden bg-[#0a0a0a] text-white"
      data-testid="business-card-page"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 jn-grid-bg opacity-30 pointer-events-none" />
      <div
        className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(0,229,255,0.20), transparent)",
        }}
      />
      <div
        className="absolute -bottom-48 -right-40 w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(197,160,89,0.18), transparent)",
        }}
      />

      <div className="relative max-w-md md:max-w-lg mx-auto px-5 sm:px-6 pt-10 pb-16">
        {/* Brand bar */}
        <div className="flex items-center justify-between mb-8">
          <a
            href="/"
            className="font-heading text-xl tracking-[0.18em] font-medium text-white"
            data-testid="card-brand-link"
          >
            JY<span style={{ color: "#00E5FF" }}>·</span>NOVA
          </a>
          <span className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">
            Digital card
          </span>
        </div>

        {/* Hero photo */}
        <div className="relative rounded-[28px] overflow-hidden border border-white/10 jn-noise">
          <img
            src={person.photo}
            alt={person.name}
            className="block w-full h-auto aspect-[4/5] object-cover"
            draggable="false"
            data-testid="card-photo"
          />
          {/* Subtle bottom gradient for legibility, NO text/badge over face */}
          <div
            className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.85) 100%)",
            }}
          />
        </div>

        {/* Identity */}
        <div className="mt-8">
          <div
            className="text-[11px] font-bold uppercase tracking-[0.28em] text-cyan-400"
            data-testid="card-role"
          >
            {person.role} — JYNOVA
          </div>
          <h1
            className="font-heading mt-3 text-4xl sm:text-5xl font-light leading-[1.05] tracking-tight"
            data-testid="card-name"
          >
            {person.name.split(" ")[0]}{" "}
            <span
              className="italic font-medium"
              style={{ color: "#C5A059" }}
            >
              {person.name.split(" ").slice(1).join(" ")}
            </span>
          </h1>
          <p className="mt-4 text-sm text-zinc-400 max-w-sm leading-relaxed">
            Slimme oppervlakken voor woningen en bedrijven — Privacy Smart Film,
            Display Glass & Smart Wall Panels.
          </p>
        </div>

        {/* Primary action — Save contact */}
        <a
          href={person.vcf}
          download
          className="jn-btn-primary mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-sm sm:text-base"
          data-testid="card-save-contact"
        >
          <Download size={18} />
          Contact opslaan
        </a>

        {/* Action grid */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <ActionTile
            href={telHref}
            icon={<Phone size={18} />}
            label="Bellen"
            value={person.phone}
            testid="card-action-call"
          />
          <ActionTile
            href={waHref}
            icon={<MessageCircle size={18} />}
            label="WhatsApp"
            value="Stuur bericht"
            external
            testid="card-action-whatsapp"
          />
          <ActionTile
            href={mailHref}
            icon={<Mail size={18} />}
            label="E-mail"
            value={person.email}
            testid="card-action-email"
          />
          <ActionTile
            href={siteHref}
            icon={<Globe size={18} />}
            label="Website"
            value={person.website.replace(/^https?:\/\//, "")}
            external
            testid="card-action-website"
          />
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-white/10 flex items-center justify-between text-[11px] text-zinc-500">
          <span className="uppercase tracking-[0.25em]">België · 2026</span>
          <a
            href="/"
            className="text-zinc-300 hover:text-cyan-400 transition-colors inline-flex items-center gap-1"
            data-testid="card-footer-home"
          >
            jynova.be <ArrowUpRight size={12} />
          </a>
        </div>
      </div>
    </main>
  );
}

function ActionTile({ href, icon, label, value, external, testid }) {
  const props = external
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <a
      href={href}
      {...props}
      className="group relative rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-cyan-400/50 transition-all p-4 flex flex-col gap-2"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span
          className="w-9 h-9 rounded-full flex items-center justify-center bg-black/60 border border-white/10 text-cyan-400 group-hover:text-white group-hover:border-cyan-400/60 transition-colors"
          aria-hidden="true"
        >
          {icon}
        </span>
        <ArrowUpRight
          size={14}
          className="text-zinc-600 group-hover:text-cyan-400 transition-colors"
        />
      </div>
      <div className="mt-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          {label}
        </div>
        <div className="text-sm text-white font-medium truncate mt-0.5">
          {value}
        </div>
      </div>
    </a>
  );
}
